/**
 * PasswordTools — helpers pentru câmpuri de parolă:
 *   - PasswordStrength: bar 0-4 + descriere (zxcvbn-ts)
 *   - PasswordGeneratorButton: popover cu slider + toggles → generează parolă
 *
 * Ambele funcționează 100% client-side. zxcvbn-ts e bundle-uit (~80 KB
 * gzip cu limba en) — acceptabil pt securitate vizibilă imediat.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as zxcvbnCommon from '@zxcvbn-ts/language-common';
import * as zxcvbnEn from '@zxcvbn-ts/language-en';

import PasswordStrengthBar from '@/components/ui/PasswordStrengthBar';

// Setup o singură dată la module-load
let _inited = false;
function ensureZxcvbnInit() {
  if (_inited) return;
  zxcvbnOptions.setOptions({
    translations: zxcvbnEn.translations,
    graphs: zxcvbnCommon.adjacencyGraphs,
    dictionary: {
      ...zxcvbnCommon.dictionary,
      ...zxcvbnEn.dictionary,
    },
  });
  _inited = true;
}

// ============================================================
// Strength meter
// ============================================================

export function PasswordStrength({ password }: { password: string }) {
  const { t } = useTranslation();
  useEffect(() => {
    ensureZxcvbnInit();
  }, []);

  const result = useMemo(() => {
    if (!password) return null;
    try {
      ensureZxcvbnInit();
      return zxcvbn(password);
    } catch {
      return null;
    }
  }, [password]);

  if (!password) return null;

  const score = result?.score ?? 0;
  const warning = result?.feedback?.warning;
  const suggestions = result?.feedback?.suggestions ?? [];
  const crackTime = result?.crackTimesDisplay?.offlineSlowHashing1e4PerSecond;

  return (
    <div className="mt-2 space-y-1.5">
      <PasswordStrengthBar score={score} compact />
      {crackTime && (
        <div
          className="text-[10px] font-mono"
          style={{ color: 'var(--text-dim)' }}
        >
          {t('vault_ui.tools.crack_offline', { time: String(crackTime) })}
        </div>
      )}
      {warning && (
        <div className="text-[10px]" style={{ color: 'var(--danger)' }}>
          ⚠ {warning}
        </div>
      )}
      {suggestions.length > 0 && (
        <ul
          className="text-[10px] list-disc pl-4"
          style={{ color: 'var(--text-muted)' }}
        >
          {suggestions.map((s, i) => (
            <li key={i}>{s}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ============================================================
// Generator
// ============================================================

const ALPHA_LOWER = 'abcdefghijklmnopqrstuvwxyz';
const ALPHA_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const DIGITS = '0123456789';
// Omit ambiguous/problematic symbols (backtick, backslash, etc.)
const SYMBOLS = '!@#$%^&*()_+-=[]{};:,.<>?';

interface GenOpts {
  length: number;
  upper: boolean;
  lower: boolean;
  digits: boolean;
  symbols: boolean;
}

function generatePassword(opts: GenOpts): string {
  const pools: string[] = [];
  if (opts.lower) pools.push(ALPHA_LOWER);
  if (opts.upper) pools.push(ALPHA_UPPER);
  if (opts.digits) pools.push(DIGITS);
  if (opts.symbols) pools.push(SYMBOLS);
  if (pools.length === 0) return '';

  const all = pools.join('');
  const out: string[] = [];

  // Garantăm cel puțin un caracter din fiecare pool selectat
  for (const p of pools) {
    out.push(randomChar(p));
  }
  while (out.length < opts.length) {
    out.push(randomChar(all));
  }

  // Shuffle Fisher-Yates (cripto-random)
  for (let i = out.length - 1; i > 0; i--) {
    const r = cryptoRandomInt(i + 1);
    [out[i], out[r]] = [out[r], out[i]];
  }
  return out.slice(0, opts.length).join('');
}

function cryptoRandomInt(max: number): number {
  // Rejection sampling uniform pe max
  if (max <= 0) return 0;
  const range = 0x100000000; // 2^32
  const limit = range - (range % max);
  const buf = new Uint32Array(1);
  let v: number;
  do {
    crypto.getRandomValues(buf);
    v = buf[0];
  } while (v >= limit);
  return v % max;
}

function randomChar(pool: string): string {
  return pool[cryptoRandomInt(pool.length)];
}

export function PasswordGeneratorButton({
  onGenerate,
}: {
  onGenerate: (password: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<GenOpts>({
    length: 20,
    upper: true,
    lower: true,
    digits: true,
    symbols: true,
  });
  const [preview, setPreview] = useState<string>(() => generatePassword({
    length: 20,
    upper: true,
    lower: true,
    digits: true,
    symbols: true,
  }));
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const regenerate = (nextOpts?: GenOpts) => {
    setPreview(generatePassword(nextOpts ?? opts));
  };

  const update = (patch: Partial<GenOpts>) => {
    const next = { ...opts, ...patch };
    // Evită toate off
    if (!next.upper && !next.lower && !next.digits && !next.symbols) {
      return;
    }
    setOpts(next);
    regenerate(next);
  };

  const accept = () => {
    onGenerate(preview);
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) regenerate();
        }}
        className="text-[10px] hover:underline"
        style={{ color: 'var(--text-muted)' }}
        title={t('vault_ui.tools.generate_title')}
      >
        {t('vault_ui.tools.generate')}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 rounded-md shadow-lg p-3 space-y-2.5 w-[280px]"
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
          }}
        >
          <div
            className="font-mono text-xs break-all px-2 py-1.5 rounded select-all"
            style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
            }}
          >
            {preview}
          </div>

          <div>
            <div
              className="flex items-center justify-between text-[10px] mb-1"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>{t('vault_ui.tools.length')}</span>
              <span className="font-mono tabular-nums">{opts.length}</span>
            </div>
            <input
              type="range"
              min={8}
              max={64}
              value={opts.length}
              onChange={(e) =>
                update({ length: Math.max(8, Math.min(64, Number(e.target.value))) })
              }
              className="w-full accent-[var(--accent)]"
            />
          </div>

          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px]">
            <Toggle
              label="a–z"
              checked={opts.lower}
              onChange={(v) => update({ lower: v })}
            />
            <Toggle
              label="A–Z"
              checked={opts.upper}
              onChange={(v) => update({ upper: v })}
            />
            <Toggle
              label="0–9"
              checked={opts.digits}
              onChange={(v) => update({ digits: v })}
            />
            <Toggle
              label="!@#$"
              checked={opts.symbols}
              onChange={(v) => update({ symbols: v })}
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              className="btn-primary text-xs flex-1"
              onClick={accept}
            >
              {t('vault_ui.tools.use')}
            </button>
            <button
              type="button"
              className="btn-outline text-xs"
              onClick={() => regenerate()}
              title={t('vault_ui.tools.regenerate_title')}
            >
              ↻
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-[var(--accent)]"
      />
      <span className="font-mono" style={{ color: 'var(--text)' }}>
        {label}
      </span>
    </label>
  );
}
